import { useEffect, useState } from 'react';
import { platformSettings } from '@/api/platformSettings';
import type { CnamView } from '@/api/platformSettings';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';

/**
 * Super-admin page for configuring CNAM (Caller Name) lookups.
 *
 * Provider: Telnyx Number Lookup API. Self-signup with credit card,
 * $0.003 per uncached lookup. The API key is stored encrypted at rest
 * in platform_settings; the backend reads it at call time and resolves
 * via the Telnyx CNAM lookup endpoint.
 *
 * Caching: the cnam_lookups table is platform-global (migration 0088).
 * A caller who phones in to multiple customers only generates one
 * Telnyx charge per 30 days.
 *
 * After save, the backend invalidates its in-memory provider cache so
 * the change is live without a server restart.
 */
export function CnamSettings() {
  const [current, setCurrent] = useState<CnamView | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    platformSettings.getCnam().then((v) => {
      if (ignore) return;
      setCurrent(v);
    }).catch(() => { /* leave defaults */ });
    return () => { ignore = true; };
  }, []);

  async function save() {
    setStatus(null);
    setSaving(true);
    try {
      await platformSettings.putCnam({
        provider: 'telnyx',
        api_key: apiKey,
      });
      setStatus('Saved. Inbound calls will now resolve names via Telnyx.');
      setApiKey('');
      const next = await platformSettings.getCnam();
      setCurrent(next);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    if (!confirm('Clear CNAM credentials? Inbound calls will fall back to caller profile names or raw numbers.')) return;
    setStatus(null);
    setSaving(true);
    try {
      await platformSettings.putCnam({ clear: true });
      setStatus('Cleared.');
      const next = await platformSettings.getCnam();
      setCurrent(next);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Clear failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Caller Name (CNAM)"
        description="When a call comes in, look up the caller's name from Telnyx so the call log and voicemail emails show real names instead of just phone numbers."
      />
      <div className="max-w-2xl space-y-6">
        <Card className={current?.configured ? 'border-emerald-200/60' : 'border-amber-200/60'}>
          <CardContent className="py-4">
            {current?.configured ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">CNAM is active (Telnyx)</div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    API key: {current.api_key_redacted}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-foreground">CNAM is not configured</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Inbound calls show the caller's number unless a caller profile has a saved name.
                    Add a Telnyx API key below to turn name lookups on.
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Telnyx API key</CardTitle>
            <CardDescription>
              Stored encrypted at rest. The key is never shown back to the portal — to rotate it,
              paste a new one and save.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="cnamKey">API key</Label>
              <Input
                id="cnamKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={current?.configured ? current.api_key_redacted ?? '••••••••••••••••' : 'KEYxxxxxxxxxxxxxxxx'}
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Find this in your Telnyx Mission Control Portal under
                <strong> API Keys</strong>. Use a key that has the
                <strong> Number Lookup</strong> permission enabled.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={save} disabled={saving || !apiKey}>
                {saving ? 'Saving…' : 'Save API key'}
              </Button>
              {current?.configured && (
                <Button variant="outline" onClick={clear} disabled={saving}>
                  Clear credentials
                </Button>
              )}
            </div>
            {status && <p role="alert" className="text-sm">{status}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">How it works + signup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <ol className="list-decimal ps-5 space-y-2">
              <li>
                Go to{' '}
                <a
                  href="https://telnyx.com/sign-up"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                >
                  telnyx.com/sign-up <ExternalLink className="h-3 w-3" />
                </a>
                {' '}— self-signup with a credit card, no contract or sales call.
              </li>
              <li>
                In Mission Control, go to <strong>Account &rarr; Permissions</strong> and enable
                the <strong>"Permitted NPAC User"</strong> checkbox. This certifies the lookups
                are for legitimate caller-ID use (industry rule, takes 10 seconds).
              </li>
              <li>
                Go to <strong>Account &rarr; API Keys</strong>, create a key with
                <strong> Number Lookup</strong> permission, and paste it into the form above.
              </li>
              <li>
                That's it — the next inbound call resolves through Telnyx. Cached names appear
                in <code className="rounded bg-slate-100 px-1">/portal/call-log</code> and on
                voicemail email notifications.
              </li>
            </ol>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
              <div><strong>Pricing:</strong> $0.003 per uncached lookup. Pay-as-you-go, no monthly minimum.</div>
              <div><strong>Cache:</strong> 30 days, platform-wide. A caller who phones in to two different customers
                only generates one Telnyx charge per 30 days, not two.</div>
              <div><strong>Fallback:</strong> if a caller has a saved profile display name, that wins over
                CNAM — no lookup happens for them at all.</div>
              <div><strong>Carrier independence:</strong> works for any US phone number; your phone
                numbers do NOT need to be hosted at Telnyx.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
